import { useMemo } from 'react';
import {
  isInApplicationScope,
  SIGNAL_OUT_OF_TESTING_SCOPE_ERROR,
} from '@terra/shared-asset-processing';
import type { GroupedSignal } from '@terra/shared-contracts-signals';
import type { Application } from '../store/projectStore';
import { useAppTargetId } from '../store/projectStore';
import { useApplicationById } from './api/useApplicationById';

type ApplicationTestingScopeSource = Pick<Application, 'url' | 'domains'>;

type SignalTestingScopeInput = {
  signal: Pick<GroupedSignal, 'endpoint'>;
  application?: ApplicationTestingScopeSource | null;
  isLoadingTestingScope?: boolean;
};

export function useApplicationTestingScopeSource() {
  const appTargetId = useAppTargetId();
  const { application, isLoading } = useApplicationById({
    id: appTargetId,
    enabled: Boolean(appTargetId),
  });

  return {
    application,
    isLoadingTestingScope: Boolean(appTargetId) && isLoading,
  };
}

export function getSignalTestingScopeEligibility({
  signal,
  application,
  isLoadingTestingScope,
}: SignalTestingScopeInput): {
  isEligible: boolean;
  targetUrl: string | null;
} {
  const targetUrl = signal.endpoint?.url ?? null;

  if (isLoadingTestingScope) {
    return { isEligible: true, targetUrl };
  }

  if (!application) {
    return { isEligible: true, targetUrl };
  }

  if (!targetUrl) {
    return { isEligible: true, targetUrl };
  }

  return {
    isEligible: isInApplicationScope({
      source: {
        applicationUrl: application.url,
        domains: application.domains ?? [],
      },
      url: targetUrl,
      dimension: 'testing',
    }),
    targetUrl,
  };
}

export function useSignalTestingScopeEligibility({
  signal,
}: {
  signal: Pick<GroupedSignal, 'endpoint'>;
}) {
  const { application, isLoadingTestingScope } =
    useApplicationTestingScopeSource();

  return useMemo(
    () =>
      getSignalTestingScopeEligibility({
        signal,
        application,
        isLoadingTestingScope,
      }),
    [application, isLoadingTestingScope, signal],
  );
}

export { SIGNAL_OUT_OF_TESTING_SCOPE_ERROR };
